import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Clock, EyeOff, Loader2, RotateCcw, X } from "lucide-react";
import { describeHistoryEntry, formatWhen, metaFor, roleLabel } from "./meta";

const TABS = [
  { key: "content", label: "Content" },
  { key: "changes", label: "Changes" },
  { key: "history", label: "History" },
];
const STEPS = [
  { key: "draft", label: "Teacher" },
  { key: "hod_review", label: "HOD" },
  { key: "admin_review", label: "Admin" },
  { key: "approved", label: "Approved" },
  { key: "published", label: "Published" },
];

function Stepper({ stage }) {
  const current = Math.max(0, STEPS.findIndex((s) => s.key === stage));
  return (
    <ol className="flex flex-wrap items-center gap-2 text-[11px]" aria-label="Approval progress">
      {STEPS.map((step, i) => {
        const done = i < current || stage === "published";
        const active = i === current && stage !== "published";
        return (
          <li key={step.key} className="flex items-center gap-2">
            <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold ${done ? "border-emerald-500 bg-emerald-500 text-white" : active ? "border-[#1565c0] bg-[#1565c0] text-white" : "border-slate-300 bg-white text-slate-400"}`}>
              {done ? "✓" : i + 1}
            </span>
            <span className={active ? "font-semibold text-[#1565c0]" : done ? "text-emerald-700" : "text-slate-400"}>{step.label}</span>
            {i < STEPS.length - 1 && <span className="h-px w-4 bg-slate-300" />}
          </li>
        );
      })}
    </ol>
  );
}

const show = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" || typeof v === "number") return String(v);
  return JSON.stringify(v, null, 1);
};

function ChangesTab({ changes, hasBaseline }) {
  if (!hasBaseline) return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">Never approved yet, so there is nothing to compare against.</div>;
  if (!changes.length) return <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-[13px] text-emerald-800"><CheckCircle2 className="h-4 w-4" /> No changes since the last approval.</div>;
  return (
    <div className="space-y-3">
      {changes.map((c) => (
        <div key={c.field} className="rounded-xl border border-[#e6edf8] bg-white p-3">
          <div className="mb-2 text-[13px] font-semibold text-slate-800">{c.field}</div>
          <div className="grid grid-cols-1 gap-2 text-[12px] sm:grid-cols-2">
            <pre className="m-0 whitespace-pre-wrap rounded-lg bg-rose-50 p-2 font-[inherit] text-rose-900"><b className="block text-[10px] uppercase text-rose-700">Before</b>{show(c.before)}</pre>
            <pre className="m-0 whitespace-pre-wrap rounded-lg bg-emerald-50 p-2 font-[inherit] text-emerald-900"><b className="block text-[10px] uppercase text-emerald-700">Now</b>{show(c.after)}</pre>
          </div>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({ history }) {
  if (!history.length) return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-500">No recorded activity yet.</div>;
  return (
    <ol className="relative ml-2 border-l border-slate-200">
      {[...history].reverse().map((h, i) => (
        <li key={`${h.at}-${i}`} className="mb-4 ml-4">
          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border border-white bg-[#1565c0]" />
          <div className="text-[13px] font-semibold text-slate-800">{describeHistoryEntry(h)}</div>
          <div className="text-[11px] text-slate-500">{h.actor_name || "Unknown"} · {roleLabel(h.role)} · {formatWhen(h.at)}{h.batch_id ? " · bulk" : ""}</div>
          {h.reason && <div className="mt-1 rounded-lg bg-slate-50 p-2 text-[12px] text-slate-700">{h.reason}</div>}
        </li>
      ))}
    </ol>
  );
}

/**
 * Generic right-hand review drawer. `config` supplies: entityLabel, api.detail/review/unpublish,
 * renderContent(detail), titleOf(summary), subtitleOf(summary). Every decision sends the revision it
 * was loaded at, so an item edited after load is rejected by the server instead of approved blind.
 */
export default function ReviewDrawer({ config, id, onClose, onDecided, onPrev, onNext, position }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("content");
  const [mode, setMode] = useState(null); // approve | changes | override | unpublish
  const [understood, setUnderstood] = useState(false);
  const [reason, setReason] = useState("");
  const [sendBackTo, setSendBackTo] = useState("hod");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const panelRef = useRef(null);
  const noun = config.entityLabel;

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setDetail(null); setMode(null); setUnderstood(false); setReason(""); setActionError(""); setTab("content");
    config.api.detail(id)
      .then((r) => !cancelled && setDetail(r))
      .catch((e) => !cancelled && setError(e.message || `Could not load this ${noun}.`))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id, config, noun]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const s = detail?.summary;
  const meta = useMemo(() => (s ? metaFor(s.display_status, s.stage) : null), [s]);
  const isAdminGate = s?.stage === "admin_review";
  const canDecide = s && (s.can_approve || s.can_request_changes || s.can_override_hod || s.can_unpublish);

  const submit = async () => {
    if (mode === "changes" && !reason.trim()) return setActionError("Tell the teacher what to change. A reason is required.");
    if (mode === "override" && (!reason.trim() || !understood)) return setActionError("Explain why the HOD step is being skipped and tick the confirmation box.");
    setBusy(true); setActionError("");
    try {
      if (mode === "unpublish") await config.api.unpublish(s.id);
      else await config.api.review(s.id, {
        action: mode === "approve" ? "approve" : mode === "override" ? "override_hod" : "request_changes",
        confirm_override: mode === "override",
        reason: reason.trim(),
        send_back_to: mode === "changes" && s.can_send_back_to_teacher ? sendBackTo : undefined,
        expected_revision: s.revision,
      });
      onDecided(s, mode);
    } catch (e) {
      setActionError(e.message || "The decision could not be saved.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Review ${noun}`}>
      <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Close review" onClick={() => !busy && onClose()} />
      <div ref={panelRef} tabIndex={-1} className="relative flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl outline-none" style={{ fontFamily: "Poppins, system-ui, sans-serif" }}>
        <header className="border-b border-[#e6edf8] bg-gradient-to-br from-white to-[#f4f8fe] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {s ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="m-0 text-[18px] font-bold text-slate-900">{config.titleOf(s)}</h2>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.pill}`}>{meta.label}</span>
                    {(detail.changes_since_approval || []).length > 0 && <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">Edited since approval</span>}
                  </div>
                  <p className="mt-1 text-[12px] text-slate-500">{config.subtitleOf(s)} · {s.teacher_name || "Unknown teacher"} · revision {s.revision}{s.submitted_at ? ` · submitted ${formatWhen(s.submitted_at)}` : ""}</p>
                </>
              ) : <h2 className="m-0 text-[18px] font-bold text-slate-900">Review {noun}</h2>}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {position && <span className="mr-1 text-[11px] text-slate-500">{position}</span>}
              <button type="button" onClick={onPrev} disabled={!onPrev || busy} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30" aria-label={`Previous ${noun}`}><ChevronLeft className="h-5 w-5" /></button>
              <button type="button" onClick={onNext} disabled={!onNext || busy} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30" aria-label={`Next ${noun}`}><ChevronRight className="h-5 w-5" /></button>
              <button type="button" onClick={onClose} disabled={busy} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
          </div>
          {s && <div className="mt-3"><Stepper stage={s.stage} /></div>}
        </header>

        {s?.last_changes_requested && s.display_status === "changes_requested" && (
          <div className="flex gap-2 border-b border-rose-200 bg-rose-50 px-5 py-3 text-[12px] text-rose-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div><span className="font-semibold">{s.last_changes_requested.by}</span> asked for changes{s.last_changes_requested.at ? ` · ${formatWhen(s.last_changes_requested.at)}` : ""}:
              <div className="mt-0.5 whitespace-pre-wrap">{s.last_changes_requested.reason}</div></div>
          </div>
        )}

        <nav className="flex gap-1 border-b border-[#e6edf8] px-5" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-[13px] font-medium ${tab === t.key ? "border-[#1565c0] text-[#1565c0]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t.label}
              {t.key === "changes" && (detail?.changes_since_approval || []).length > 0 && <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">{detail.changes_since_approval.length}</span>}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto bg-[#f9fbff] px-5 py-4">
          {loading && <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-800">{error}</div>}
          {detail && !loading && (
            <>
              {tab === "content" && config.renderContent(detail)}
              {tab === "changes" && <ChangesTab changes={detail.changes_since_approval || []} hasBaseline={!!detail[config.entityKey]?.approved_snapshot} />}
              {tab === "history" && <HistoryTab history={detail.status_history || []} />}
            </>
          )}
        </div>

        {detail && (
          <footer className="border-t border-[#e6edf8] bg-white px-5 py-3">
            {!canDecide && (
              <p className="m-0 flex items-center gap-1.5 text-[12px] text-slate-500"><Clock className="h-3.5 w-3.5" />
                {s.stage === "published" ? `This ${noun} is published.` : s.stage === "approved" ? `Approved. The teacher decides when to publish it.` : s.stage === "draft" ? "Waiting for the teacher to resubmit." : "Waiting on the other review stage, or you authored this item."}
              </p>
            )}
            {s.can_override_hod && !mode && (
              <p className="m-0 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                {s.hod_assigned ? `This ${noun} is waiting for its HOD. ` : "No HOD is assigned for this teacher. "}Only if the HOD is unavailable, you can skip the HOD step for this one {noun}.
              </p>
            )}
            {canDecide && !mode && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {s.can_unpublish && <button type="button" onClick={() => setMode("unpublish")} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><EyeOff className="h-4 w-4" /> Unpublish</button>}
                {s.can_request_changes && <button type="button" onClick={() => setMode("changes")} className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300 bg-white px-4 py-2 text-[13px] font-semibold text-rose-700 hover:bg-rose-50"><RotateCcw className="h-4 w-4" /> Request changes</button>}
                {s.can_override_hod && <button type="button" onClick={() => setMode("override")} className="inline-flex items-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2 text-[13px] font-semibold text-white hover:bg-amber-600"><AlertTriangle className="h-4 w-4" /> Skip HOD approval…</button>}
                {s.can_approve && <button type="button" onClick={() => setMode("approve")} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700"><CheckCircle2 className="h-4 w-4" /> {isAdminGate ? `Approve ${noun}` : "Approve and send to admin"}</button>}
              </div>
            )}
            {canDecide && mode && (
              <div>
                {mode === "override" ? (
                  <>
                    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-950">
                      <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Are you sure you want to skip the HOD approval?</div>
                      This moves <strong>only this {noun}</strong> to admin review without the HOD's approval. It is recorded in its history as an admin override and the HOD is notified. You will still need to approve it separately.
                    </div>
                    <label className="mb-1 block text-[12px] font-semibold text-slate-700" htmlFor="ap-override">Why is the HOD step being skipped? <span className="text-rose-600">*</span></label>
                    <textarea id="ap-override" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="e.g. HOD is on leave until next month." className="w-full rounded-xl border border-slate-300 p-2.5 text-[13px] focus:border-[#1565c0] focus:outline-none focus:ring-2 focus:ring-[#1565c0]/20" />
                    <label className="mt-2 flex items-start gap-2 text-[12px] text-slate-700"><input type="checkbox" className="mt-0.5" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} /> I understand this skips the HOD approval for this {noun} only.</label>
                  </>
                ) : mode === "approve" ? (
                  <p className="m-0 mb-2 text-[13px] text-slate-700">{isAdminGate ? `Approving confirms this ${noun}. It stays private until the teacher publishes it.` : "Approving sends this to the admin for final approval."}</p>
                ) : mode === "unpublish" ? (
                  <p className="m-0 mb-2 text-[13px] text-slate-700">Students will no longer see this {noun}. It stays approved and the teacher can publish it again.</p>
                ) : (
                  <>
                    <label className="mb-1 block text-[12px] font-semibold text-slate-700" htmlFor="ap-reason">What needs to change? <span className="text-rose-600">*</span></label>
                    <textarea id="ap-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="Be specific so the teacher can fix it in one pass." className="w-full rounded-xl border border-slate-300 p-2.5 text-[13px] focus:border-[#1565c0] focus:outline-none focus:ring-2 focus:ring-[#1565c0]/20" />
                    {s.can_send_back_to_teacher && (
                      <div className="mt-2 flex gap-4 text-[12px] text-slate-700">
                        <label className="flex items-center gap-1.5"><input type="radio" checked={sendBackTo === "hod"} onChange={() => setSendBackTo("hod")} /> Back to HOD</label>
                        <label className="flex items-center gap-1.5"><input type="radio" checked={sendBackTo === "teacher"} onChange={() => setSendBackTo("teacher")} /> Straight to teacher</label>
                      </div>
                    )}
                  </>
                )}
                {actionError && <div className="mt-2 rounded-lg bg-rose-50 p-2 text-[12px] text-rose-800">{actionError}</div>}
                <div className="mt-3 flex justify-end gap-2">
                  <button type="button" disabled={busy} onClick={() => { setMode(null); setActionError(""); }} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                  <button type="button" disabled={busy} onClick={submit} className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-60 ${mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : mode === "override" ? "bg-amber-500 hover:bg-amber-600" : mode === "unpublish" ? "bg-slate-700 hover:bg-slate-800" : "bg-rose-600 hover:bg-rose-700"}`}>
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {mode === "approve" ? "Confirm approval" : mode === "override" ? "Skip HOD and continue" : mode === "unpublish" ? "Unpublish" : "Send back"}
                  </button>
                </div>
              </div>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
